import { useMemo } from 'react';

import type { FindOrdersRequestParams } from '~entities/order';
import { useFindOrdersQuery } from '~entities/order';
import type { Pos } from '~entities/pos';
import { isOnlinePos, useGetPointsOfSalesQuery } from '~entities/pos';

import { keepPreviousData } from '@tanstack/react-query';

import type { OrdersFiltersValue } from './orders-filters';
import type { OrderTableData } from './orders-table-data';

import { buildOrdersFilter } from '../lib/build-orders-filter';

type SuccessfulReturn = {
  isSuccess: true;
  data: { orders: OrderTableData[]; total: number };
};

type UnsuccessfulReturn = {
  isSuccess: false;
  data: undefined;
};

type DefaultReturn = {
  isLoading: boolean;
  isError: boolean;
};

type UseOrdersTableReturn = DefaultReturn &
  (SuccessfulReturn | UnsuccessfulReturn);

type UseOrdersTableParams = Pick<
  FindOrdersRequestParams,
  'page' | 'perPage' | 'sort'
> & {
  filters: OrdersFiltersValue;
};

export function useOrdersTableDataQuery({
  page,
  perPage,
  filters,
  sort,
}: UseOrdersTableParams): UseOrdersTableReturn {
  const posListQuery = useGetPointsOfSalesQuery();

  const onlinePosList = useMemo(
    () => posListQuery.data?.filter(isOnlinePos) ?? [],
    [posListQuery.data]
  );

  const filter = useMemo(
    () => buildOrdersFilter(filters, onlinePosList),
    [filters, onlinePosList]
  );

  const ordersQuery = useFindOrdersQuery(
    { filter, page, perPage, sort },
    {
      enabled: posListQuery.isSuccess,
      placeholderData: keepPreviousData,
    }
  );

  const posMap = useMemo(
    () =>
      onlinePosList.reduce(
        (result, pos) => {
          result[pos.id] = pos;

          return result;
        },
        {} as Record<string, Pos>
      ),
    [onlinePosList]
  );

  const queries = [posListQuery, ordersQuery];
  const isQueryLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);
  const isFetching = queries.some((q) => q.isFetching);

  const isLoading =
    isQueryLoading || (isFetching && ordersQuery.isPlaceholderData);

  if (posListQuery.isSuccess && ordersQuery.isSuccess) {
    const { orders, total } = ordersQuery.data;
    const enrichedOrders: OrderTableData[] = orders.map((order) => {
      const pos = posMap[order.pointOfSalesId];

      return {
        ...order,
        posName: pos?.name ?? '',
        merchantName: pos?.merchantName ?? '',
      };
    });

    return {
      isSuccess: true,
      data: {
        orders: enrichedOrders,
        total,
      },
      isLoading,
      isError,
    };
  }

  return {
    isSuccess: false,
    data: undefined,
    isLoading,
    isError,
  };
}
